import { c as _c } from "react/compiler-runtime";
import { useCallback, useId, type MouseEvent, type ReactElement } from 'react';
import Button from '../Button';
import { CancelBkgIcon } from '../icons/CancelBkgIcon';
import { DownloadIcon } from '../icons/DownloadIcon';
import Modal from '../Modal';
import { StyledActions, StyledFilename, StyledHeader, StyledImage, StyledImageArea } from './styles';
import { type LightboxProps } from './types';

/**
 * One file, full screen — a solid header (name + actions) over a neutral
 * panel holding the picture. Built on `Modal`; unlike `PopUp`, there is no
 * card chrome, because the content is meant to fill the viewport: the image
 * itself is the thing being looked at, not a card floating over a dimmed
 * page behind it.
 *
 * That means there is no visible "background" to click — the dismissible
 * region is the opaque padding `StyledImageArea` puts around the image, not
 * a scrim peeking out from behind a panel. Click-to-dismiss mirrors `Modal`'s
 * own contract: clicking anywhere on the surface closes, so the header and
 * the picture call `stopPropagation` to carve themselves out as the two
 * non-dismissible regions.
 */
const Lightbox = t0 => {
  const $ = _c(26);
  const {
    imageUrl,
    imageAlt,
    filename,
    onClose,
    closeLabel: t1,
    onDownload,
    downloadLabel: t2,
    onImageError,
    blurBackdrop: t3
  } = t0;
  const closeLabel = t1 === undefined ? "Close" : t1;
  const downloadLabel = t2 === undefined ? "Download" : t2;
  const blurBackdrop = t3 === undefined ? true : t3;
  const filenameId = useId();
  const handleContentClick = _temp;
  let t4;
  if ($[0] !== filename || $[1] !== filenameId) {
    t4 = <StyledFilename id={filenameId} variant="h3" color="primary">{filename}</StyledFilename>;
    $[0] = filename;
    $[1] = filenameId;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== downloadLabel || $[4] !== onDownload) {
    t5 = onDownload != null && <Button variant="outlined" scheme="light" leftIcon={<DownloadIcon />} onClick={onDownload}>{downloadLabel}</Button>;
    $[3] = downloadLabel;
    $[4] = onDownload;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <CancelBkgIcon />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== closeLabel || $[8] !== onClose) {
    t7 = <Button autoFocus={true} variant="text" scheme="light" rightIcon={t6} onClick={onClose}>{closeLabel}</Button>;
    $[7] = closeLabel;
    $[8] = onClose;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t5 || $[11] !== t7) {
    t8 = <StyledActions>{t5}{t7}</StyledActions>;
    $[10] = t5;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t4 || $[14] !== t8) {
    t9 = <StyledHeader onClick={handleContentClick}>{t4}{t8}</StyledHeader>;
    $[13] = t4;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== imageAlt || $[17] !== imageUrl || $[18] !== onImageError) {
    t10 = <StyledImageArea><StyledImage src={imageUrl} alt={imageAlt} onClick={handleContentClick} onError={onImageError} /></StyledImageArea>;
    $[16] = imageAlt;
    $[17] = imageUrl;
    $[18] = onImageError;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== blurBackdrop || $[21] !== filenameId || $[22] !== onClose || $[23] !== t10 || $[24] !== t9) {
    t11 = <Modal open={true} onClose={onClose} onClick={onClose} ariaLabelledBy={filenameId} blurBackdrop={blurBackdrop}>{t9}{t10}</Modal>;
    $[20] = blurBackdrop;
    $[21] = filenameId;
    $[22] = onClose;
    $[23] = t10;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
};
export default Lightbox;
function _temp(event) {
  event.stopPropagation();
}